<template>
    <v-sheet>
        <v-dialog :width="loading.data ? 350 : 900" persistent v-model="display">
            <v-card flat :height="loading.data ? 80 : null">
                <v-toolbar flat color="primary" dark v-if="!loading.data" dense>
                    <v-toolbar-title>{{ $t("payment_details") }}</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <p-icon-btn icon="mdi-printer" tooltip="Print" @click="openPaymentPrintDialog"></p-icon-btn>
                    <p-icon-btn icon="mdi-form-select" tooltip="Simulate Entry" @click="simulate" :loading="loading.simulate"></p-icon-btn>
                    <v-menu offset-y>
                        <template v-slot:activator="{ on }">
                            <v-btn icon v-on="on" class="mx-1">
                                <v-icon>mdi-dots-vertical</v-icon>
                            </v-btn>
                        </template>
                        <v-list width="250" dense>
                            <v-list-item @click="simulate" :disabled="!payment.meta.can_post" link>
                                <v-list-item-icon>
                                    <v-icon>mdi-form-select</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>{{ $t("simulate_entry") }}</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="openPaymentPrintDialog" link>
                                <v-list-item-icon>
                                    <v-icon>mdi-printer</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>{{ $t("print") }}</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </v-toolbar>
                <div class="text-center d-flex flex-column justify-center align-center pa-2" style="height: 100%" v-if="loading.data">
                    <v-progress-linear height="4" indeterminate> </v-progress-linear>
                    <div class="text-body-1 pa-2">Please Wait ...</div>
                </div>
                <v-card-text v-else class="mt-4">
                    <v-alert v-if="loading.simulate" text type="info" dense v-html="aicaSimulatingAlert"></v-alert>
                    <v-row>
                        <v-col cols="12" lg="6">
                            <v-subheader>General Info</v-subheader>
                            <v-simple-table dense>
                                <tbody>
                                    <tr>
                                        <th>{{ $t("code_") }}</th>
                                        <td colspan="7">{{ payment.code }}</td>
                                    </tr>
                                    <tr>
                                        <th>{{ $t("date") }}</th>
                                        <td colspan="3">{{ moment(payment.created_at).format('YYYY-MM-DD') }}</td>
                                    </tr>
                                    <tr>
                                        <th>{{ $t("document_date") }}</th>
                                        <td colspan="3">{{ payment.date }}</td>
                                    </tr>
                                    <tr>
                                        <th>{{ $t("state") }}</th>
                                        <td colspan="3">
                                            <p-status :state="payment.state"></p-status>
                                        </td>
                                    </tr>
                                </tbody>
                            </v-simple-table>
                        </v-col>
                        <v-col cols="12" lg="6">
                            <v-subheader>Customer Info</v-subheader>
                            <v-simple-table dense>
                                <tbody>
                                    <tr>
                                        <th>{{ $t("code") }}</th>
                                        <td colspan="3">{{ customer.code }}</td>
                                    </tr>
                                    <tr>
                                        <th>{{ $t("name") }}</th>
                                        <td colspan="3">
                                            {{ customer.name }}
                                            <span>
                                                <v-btn icon x-small @click="openCustomerDisplayDialog">
                                                    <v-icon>mdi-magnify</v-icon>
                                                </v-btn>
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>{{ $t("phone") }}</th>
                                        <td colspan="3">{{ customer.phone }}</td>
                                    </tr>
                                    <tr>
                                        <th>{{ $t("address") }}</th>
                                        <td colspan="3">{{ customer.address }}</td>
                                    </tr>
                                </tbody>
                            </v-simple-table>
                        </v-col>
                    </v-row>
                    <br />
                    <v-simple-table dense>
                        <thead>
                            <tr>
                                <th>{{ $t("mode") }}</th>
                                <th>{{ $t("description") }}</th>
                                <th>{{ $t("amount") }}</th>
                                <th>{{ $t("exchange_rate") }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>{{ payment.payment_mode }}</td>
                                <td>{{ payment.description }}</td>
                                <td>{{ formatCurrency(payment.amount) }}</td>
                                <td>{{ formatNumber(payment.exchange_rate, 8) }}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                    <br />
                    <v-simple-table dense>
                        <thead>
                            <tr>
                                <th>{{ $t("created_by") }}</th>
                                <th>{{ $t("created_at") }}</th>
                                <th>{{ $t("updated_by") }}</th>
                                <th>{{ $t("updated_at") }}</th>
                                <th>{{ $t("posted_by") }}</th>
                                <th>{{ $t("posted_at") }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>{{ payment.created_by }}</td>
                                <td>{{ moment(payment.created_at).format("YYYY-MM-DD HH:MM:SS") }}</td>
                                <td>{{ payment.updated_by }}</td>
                                <td>{{ moment(payment.updated_at).format("YYYY-MM-DD HH:MM:SS") }}</td>
                                <td>{{ payment.posted_by }}</td>
                                <td>{{ payment.posted_at }}</td>
                                <td></td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-card-text>
                <v-card-actions v-if="!loading.data">
                    <v-btn text @click="close">
                        {{ $t("close_btn") }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <RetryDialog ref="retryDialog" />
        <PaymentPrintDialog ref="paymentPrintDialog" />
        <simulate-entries v-if="simulateEntryDisplay" v-model="simulateEntryDisplay" :entry="simulatedEntry" @post="postPayment" :is-posted="isPosted" :can-post="canPost" :is-posting="loading.post"> </simulate-entries>
        <decision-dialog ref="decisionDialog" @submit="onSubmitDecision"> </decision-dialog>
        <customer-display-dialog ref="customerDisplayDialog"> </customer-display-dialog>
        <AlertDialog ref="alertDialog" />
    </v-sheet>
</template>

<script>
import { mapActions } from "vuex";
import { formatMoney, formatNumber } from "accounting";
import moment from "moment";

import { getDpk } from "../../services/sales";
import { getPayment, postPaymentToLedger, simulatePayment } from "../../services/sales/payment";
import { getCustomer } from "../../services/sales/customer";
import { aicaSimulatingAlert } from "../../assets/text/alerts";

import RetryDialog from "@/components/common/RetryDialog";
import PaymentPrintDialog from "../Sales/PaymentPrintDialog";
import SimulateEntries from "../accounting/SimulateEntries.vue";
import getColor from "@/utils/getColor";
import PStatus from "../common/PStatus.vue";
import DecisionDialog from "@/components/common/DecisionDialog";
import CustomerDisplayDialog from "./CustomerDisplayDialog.vue";
import PIconBtn from "../common/PIconBtn.vue";
import AlertDialog from "@/components/common/AlertDialog";

const POSTED = "POSTED";

export default {
    name: "PaymentDisplayDialog",
    components: {
        RetryDialog,
        PaymentPrintDialog,
        SimulateEntries,
        PStatus,
        DecisionDialog,
        CustomerDisplayDialog,
        PIconBtn,
        AlertDialog,
    },
    data() {
        return {
            display: false,
            openParams: null,
            customer: null,
            loading: {
                data: true,
                simulate: false,
                post: false,
            },
            simulateEntryDisplay: false,
            payment: {},
            simulatedEntry: {},
            aicaSimulatingAlert: aicaSimulatingAlert,
        };
    },
    computed: {
        isPosted() {
            return this.payment.state == POSTED;
        },
        canPost() {
            return this.payment.meta.can_post;
        },
    },
    methods: {
        moment,
        getColor,
        formatNumber,
        formatCurrency(amount) {
            return formatMoney(amount, this.payment.currency + " ", 2);
        },
        ...mapActions("common/snackBar", ["setSnackBars"]),
        open(params) {
            this.openParams = params;
            this.loading.data = true;
            this.display = true;
            this.openDataFetch();
        },
        async openDataFetch() {
            try {
                getDpk().then((response) => (this.dpk = response.data.key));
                let response = await getPayment({ id: this.openParams.id });
                this.payment = response.data;
                let { data } = await getCustomer(this.payment.customer_id);
                this.customer = data;
                this.loading.data = false;
            } catch (error) {
                let message = error.response ? error.response.data.message : error;
                this.$refs.retryDialog.open({ message });
                this.display = false;
            }
        },
        simulate() {
            this.loading.simulate = true;
            simulatePayment({ paymentId: this.payment.id })
                .then((response) => {
                    this.simulatedEntry = response.data;
                    this.openSimulateEntries();
                })
                .catch((error) => {
                    this.loading.simulate = false;

                    let message = error.response ? error.response.data.message : error;
                    let errors = error.response ? error.response.data.errors : [];
                    message += "\n";
                    message += errors.join();
                    this.$refs.alertDialog.open({ type: "error", message });
                })
                .finally(() => {
                    this.loading.simulate = false;
                });
        },
        async openSimulateEntries() {
            this.simulateEntryDisplay = true;
        },
        async closeSimulateEntries() {
            this.simulateEntryDisplay = false;
        },
        openDecisionDialog() {
            let params = { title: this.$t("confirm_title"), content: this.$t("are_you_sure_posted"), target: "post" };
            this.$refs.decisionDialog.open(params);
        },
        onSubmitDecision(params) {
            if (params.target == "post") this.postPayment();
        },
        postPayment() {
            this.loading.post = true;
            postPaymentToLedger({ id: this.payment.id, dpk: this.dpk })
                .then((response) => {
                    let payment = response.data.record;
                    this.payment = payment;
                    this.$emit("updated:payment", payment);
                    this.setSnackBars({ message: this.$t("msg_post") });
                    this.loading.post = false;
                })
                .catch((error) => {
                    this.loading.post = false;

                    let message = error.response ? error.response.data.message : error;
                    let errors = error.response ? error.response.data.errors : [];

                    message += "\n";
                    message += errors.join();
                    this.$refs.alertDialog.open({ type: "error", message });
                })
                .finally(() => {
                    this.closeSimulateEntries();
                    // this.loading.post = true;
                });
        },
        openPaymentPrintDialog() {
            this.$refs.paymentPrintDialog.open(this.payment);
        },
        openCustomerDisplayDialog() {
            this.$refs.customerDisplayDialog.open({ id: this.customer.id });
        },
        close() {
            this.display = false;
        },
    },
};
</script>
